import React, { useRef, useMemo } from "react";
import {
  Card,
  Typography,
  Row,
  Col,
  Tag,
  Button,
  Flex,
  Divider,
  Space,
  InputRef,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { JobDescriptionEntity } from "../../../../../../../Entities/JobDescription";
import JobShareSection from "../../../../../../../Components/JobShareSection";
import dayjs from "dayjs";
import NotificationsSection from "../NotificationsSection";

const { Title, Text, Paragraph } = Typography;

interface JobInfoCardProps {
  jobPost: JobDescriptionEntity;
  themeColor?: string;
}

const JobInfoCard: React.FC<JobInfoCardProps> = ({ jobPost, themeColor }) => {
  const inputRef = useRef<InputRef>(null);
  // Format CTC with currency
  const formatCTC = () => {
    if (jobPost.ctc === undefined || jobPost.ctc === null) {
      return null;
    }

    const currency = (jobPost as any).currency || "INR";
    const ctcValue =
      typeof jobPost.ctc === "number"
        ? jobPost.ctc
        : parseFloat(String(jobPost.ctc).split(" ")[0]) || jobPost.ctc;

    // Format large numbers
    if (typeof ctcValue === "number") {
      if (ctcValue >= 10000000) {
        return `${(ctcValue / 10000000).toFixed(1)} Cr ${currency}`;
      }
      if (ctcValue >= 100000) {
        return `${(ctcValue / 100000).toFixed(1)} L ${currency}`;
      }
    }
    return `${ctcValue} ${currency}`;
  };

  // Format expiry date - memoized to avoid recalculation
  const expiryInfo = useMemo(() => {
    if (!jobPost?.endDate) {
      return {
        text: "No expiry",
        isExpired: false,
        isUrgent: false,
        hasNoDate: true,
      };
    }

    // Try parsing the date - handle both string and Date formats
    let endDate = dayjs(jobPost.endDate);

    // If dayjs parsing fails, try creating a new Date first
    if (!endDate.isValid()) {
      const dateObj = new Date(jobPost.endDate);
      if (!isNaN(dateObj.getTime())) {
        endDate = dayjs(dateObj);
      } else {
        return {
          text: "Invalid date",
          isExpired: false,
          isUrgent: false,
          hasNoDate: true,
        };
      }
    }

    const today = dayjs().startOf("day");
    const endDateStart = endDate.startOf("day");
    const daysDiff = endDateStart.diff(today, "day");

    if (daysDiff < 0) {
      return {
        text: "Expired",
        isExpired: true,
        isUrgent: true,
        hasNoDate: false,
      };
    }
    if (daysDiff === 0) {
      return {
        text: "Today",
        isExpired: false,
        isUrgent: true,
        hasNoDate: false,
      };
    }
    if (daysDiff === 1) {
      return {
        text: "Tomorrow",
        isExpired: false,
        isUrgent: true,
        hasNoDate: false,
      };
    }
    if (daysDiff <= 7) {
      return {
        text: `${daysDiff} days`,
        isExpired: false,
        isUrgent: true,
        hasNoDate: false,
      };
    }
    return {
      text: endDate.format("DD MMM YYYY"),
      isExpired: false,
      isUrgent: false,
      hasNoDate: false,
    };
  }, [jobPost?.endDate]);

  const getAppContent = () => {
    const appType = jobPost?.appType || null;
    if (!appType) {
      return null;
    }

    if (appType === "zoho") {
      return {
        img: "/images/zoho-recruit-logo.png",
        cta: "View on Zoho recruit",
      };
    } else if (appType === "lever") {
      return {
        img: "/images/lever-logo.png",
        cta: "View on Lever Hire",
      };
    } else {
      return null;
    }
  };

  const isFromATS = !!(
    jobPost &&
    jobPost?.appJobId &&
    jobPost?.appJobPostUrl &&
    jobPost?.appType
  );

  const atsAppInfo = getAppContent();

  const handleViewOnATS = () => {
    if (!jobPost?.appJobPostUrl) {
      return;
    }
    window.open(jobPost?.appJobPostUrl, "_blank");
  };

  return (
    <Card className="job-info-card" size="small">
      {/* Job Title */}
      <Typography.Title level={5} style={{ margin: 0 }}>
        {jobPost?.title}
      </Typography.Title>
      <Text type="secondary">{jobPost?.position}</Text>
      {/* Description */}
      {jobPost.description && (
        <Paragraph
          ellipsis={{ rows: 10, expandable: true, symbol: "more" }}
          style={{ marginTop: 12, marginBottom: 16 }}
        >
          {jobPost.description}
        </Paragraph>
      )}
      <Divider style={{ margin: "16px 0" }} />
      {/* Quick Stats Grid */}
      <Row gutter={[16, 12]}>
        {/* Openings */}
        <Col xs={12} sm={6}>
          <Tooltip title="Number of positions available for this role">
            <div className="stat-item">
              <div className="stat-content">

                <Flex vertical align="start" gap={4}>
                  <Flex align="center" gap={4}>

                    <TeamOutlined style={{ color: themeColor || "#1890ff" }} />
                    <Text type="secondary" className="stat-label">
                      Openings
                    </Text>
                  </Flex>
                  <Text strong className="stat-value">
                    {jobPost?.numberOfOpenings || 1}
                  </Text>
                </Flex>
              </div>
            </div>
          </Tooltip>
        </Col>

        {/* Applications */}
        <Col xs={12} sm={6}>
          <Tooltip title="Total number of candidates who have applied for this job">
            <div className="stat-item">
              <div className="stat-content">
                <Flex vertical align="start" gap={4}>
                  <Flex align="center" gap={4}>
                    <CalendarOutlined style={{ color: themeColor || "#1890ff" }} />

                    <Text type="secondary" className="stat-label">
                      Applied
                    </Text>
                  </Flex>
                  <Text strong className="stat-value">
                    {jobPost?.numberOfApplications || 0}
                  </Text>
                </Flex>
              </div>
            </div>
          </Tooltip>
        </Col>

        {/* CTC */}
        {formatCTC() && (
          <Col xs={12} sm={6}>
            <Tooltip title="Cost to Company - Annual compensation offered for this position">
              <div className="stat-item">
                <div className="stat-content">
                  <Flex vertical align="start" gap={4}>
                    <Flex align="center" gap={4}>
                      <DollarOutlined style={{ color: themeColor || "#1890ff" }} />
                      <Text type="secondary" className="stat-label">
                        CTC
                      </Text>
                    </Flex>
                    <Text strong className="stat-value">
                      {formatCTC()}
                    </Text>
                  </Flex>
                </div>
              </div>
            </Tooltip>
          </Col>
        )}

        {/* Notice Period */}
        {jobPost?.noticePeriod !== undefined &&
          jobPost?.noticePeriod !== null && (
            <Col xs={12} sm={6}>
              <Tooltip title="Expected candidate availability - notice period to serve in current company (in days)">
                <div className="stat-item">
                  <div className="stat-content">
                    <Flex vertical align="start" gap={4}>
                      <Flex align="center" gap={4}>
                        <ClockCircleOutlined
                          style={{ color: themeColor || "#1890ff" }}
                        />

                        <Text type="secondary" className="stat-label">
                          Notice
                        </Text>
                      </Flex>
                      <Text strong className="stat-value">
                        {jobPost.noticePeriod === 0
                          ? "Immediate"
                          : `${jobPost.noticePeriod}d`}
                      </Text>
                    </Flex>
                  </div>
                </div>
              </Tooltip>
            </Col>
          )}

        {/* Expires - Always visible */}
        <Col xs={12} sm={6}>
          <Tooltip
            title={
              expiryInfo.hasNoDate
                ? "No expiry date set for this job post"
                : expiryInfo.isExpired
                  ? "This job post has expired and will be automatically deactivated"
                  : expiryInfo.isUrgent
                    ? "This job post is closing soon"
                    : jobPost?.endDate
                      ? `Job post will expire on ${dayjs(jobPost.endDate).format("DD MMM YYYY")}`
                      : "No expiry date set"
            }
          >
            <div className="stat-item">

              <div className="stat-content">
                <Flex vertical align="start" gap={4}>
                  <Flex align="center" gap={4}>
                    <FieldTimeOutlined
                      style={{
                        color: expiryInfo.isExpired
                          ? "#ff4d4f"
                          : expiryInfo.isUrgent
                            ? "#faad14"
                            : expiryInfo.hasNoDate
                              ? "#d9d9d9"
                              : themeColor || "#1890ff",
                      }}
                    />
                    <Text type="secondary" className="stat-label">
                      Expires
                    </Text>
                  </Flex>
                  <Text
                    strong
                    className="stat-value"
                    style={{
                      color: expiryInfo.isExpired
                        ? "#ff4d4f"
                        : expiryInfo.isUrgent
                          ? "#faad14"
                          : expiryInfo.hasNoDate
                            ? "#bfbfbf"
                            : undefined,
                    }}
                  >
                    {expiryInfo.text}
                  </Text>
                </Flex>
              </div>
            </div>
          </Tooltip>
        </Col>
      </Row>

      <Divider style={{ margin: "16px 0 12px" }} />

      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          CANDIDATE NOTIFICATIONS
        </Text>
        <NotificationsSection
          notifications={jobPost?.notifications}
          themeColor={themeColor}
        />
      </Space>

      {isFromATS && atsAppInfo && <Divider style={{ margin: "16px 0 12px" }} />}

      {isFromATS && atsAppInfo && (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            SOURCE
          </Text>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <div className="app-card-header-icon">
              <img src={atsAppInfo.img} alt={jobPost?.appType} />
            </div>

            {/* Temporraly removed view on ATS */}
            {/* <Button
              size="middle"
              icon={<EyeOutlined />}
              onClick={handleViewOnATS}
            >
              {atsAppInfo.cta}
            </Button> */}
          </div>
        </Space>
      )}
    </Card>
  );
};

export default JobInfoCard;
